import React, { Fragment } from "react";
import {
  CashbackHistoryRequest,
  CashbackPayoutStatus,
  CashbackSingleCurrencyPayoutListResponse,
  CashbackSingleCurrencyPayoutRow,
  ListUsersRequest,
  Source,
} from "../../http/protocol";
import { ListView, ListViewProps, ListViewState } from "../ListView";
import { Form, FormListener } from "../form/Form";
import { ColumnType } from "../list/list/ColumnType";
import { EntityDataTable } from "../list/list/EntityDataTable";
import { withRouter } from "react-router";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { Submit } from "../form/Submit";
import { CashbackSingleCurrencyPayoutService } from "../../services/CashbackSingleCurrencyPayoutService";
import { t } from "../../i18n";
import { SelectInput } from "../form/SelectInput";
import { SourceBadge } from "./SourceBadge";
import { FeatureFlags } from "../../feature-flags/FeatureFlagContext";
import { withFeatureFlag } from "../../feature-flags/withFeatureFlag";

interface State extends ListViewState<CashbackHistoryRequest> {
  form?: CashbackHistoryRequest;
  validationMap?: any;
  warningFlag?: boolean;
}

interface Props extends ListViewProps {
  cashbackV2Enabled: boolean;
}

function renderDisplayId(item: CashbackSingleCurrencyPayoutRow) {
  if (item.id === undefined || item.id === null) {
    return <span />;
  }
  return <span>{item.id}</span>;
}

export class CashbackSingleCurrencyPayoutListView extends ListView<State, CashbackHistoryRequest, Props> implements FormListener<CashbackHistoryRequest> {
  columns = [
    {
      property: "source",
      name: "Source",
      type: ColumnType.COMPONENT,
      component: (item: CashbackSingleCurrencyPayoutRow) => <SourceBadge source={item.source} />,
    },
    {
      property: "id",
      name: "Id",
      type: ColumnType.COMPONENT,
      orderBy: "id",
      component: (item: CashbackSingleCurrencyPayoutRow) => renderDisplayId(item),
    },
    {
      property: "userId",
      name: "User ID",
      type: ColumnType.LINK,
      getUrl: (item: CashbackSingleCurrencyPayoutRow) => "/user/" + item.userId,
    },
    { property: "cashbackLogId", name: "Cashback Log ID" },
    { property: "currency", name: "Currency" },
    { property: "amount", name: "Amount" },
    { property: "status", name: "Status" },
    { property: "created", name: "Created", type: ColumnType.DATE, orderBy: "createdAt" },
  ];

  service: CashbackSingleCurrencyPayoutService;

  constructor(props: Props) {
    super(props, "/cashback/single-currency-payout/list");
    if (!this.state.listRequest?.orderBy) {
      this.state = { ...this.state, listRequest: { ...this.state.listRequest, orderBy: "createdAt" } };
    }
    this.state = { ...this.state, validationMap: {}, form: { ...this.state.listRequest } as any };
    this.service = getContainer().getCashbackSingleCurrencyPayoutService();
  }

  formDidUpdate(formModel: ListUsersRequest, validationMap: any): void {
    this.setState({ form: formModel, validationMap: validationMap });
  }

  onSubmitError(): void {}

  onSubmit(): void | Promise<any> {
    if (this.state.form) {
      let form = this.state.form;
      form.offset = 0;
      this.updateListState(form);
    }
  }

  getTitle() {
    return "Cashback Single Currency Payouts";
  }

  renderContent() {
    if (!this.state.listRequest) {
      return <div></div>;
    }
    const columns = this.props.cashbackV2Enabled ? this.columns : this.columns.filter((column) => column.property !== "source");
    return (
      <Fragment>
        <div>
          <div className="wrapper wrapper-content">
            <div className={"card-box"}>
              <h4>Search Filters</h4>
              <Form
                formListener={this}
                modelObject={this.state.form}
                validationMap={this.state.validationMap}
              >
                <FormGroup className={"form-group"}>
                  <label>User Id</label>
                  <FormInput
                    model={"userId"}
                    className={"form-control"}
                    type={"integer"}
                    validators={[Validators.integer()]}
                  />
                </FormGroup>
                <FormGroup className={"form-group"}>
                  <label>Cashback Log Id</label>
                  <FormInput
                    model={"cashbackLogId"}
                    className={"form-control"}
                    type={"integer"}
                    validators={[Validators.integer()]}
                  />
                </FormGroup>
                {this.props.cashbackV2Enabled && (
                  <FormGroup className={"form-group"}>
                    <label>{t(`label.source`)}</label>
                    <SelectInput
                      optional={true}
                      model={"source"}
                      className={"form-control"}
                      values={Object.keys(Source)}
                    />
                  </FormGroup>
                )}
                <FormGroup className={"form-group"}>
                  <label>{t(`label.status`)}</label>
                  <SelectInput
                    optional={true}
                    model={"status"}
                    className={"form-control"}
                    values={Object.keys(CashbackPayoutStatus)}
                  />
                </FormGroup>
                <Submit className={"btn btn-primary"}>Search</Submit>
              </Form>
            </div>
            {this.props.cashbackV2Enabled && this.state.warningFlag && (
              <div
                className="alert alert-warning mt-3"
                role="alert"
              >
                {t(`cashback.singleCurrencyPayouts.partial-warning`)}
              </div>
            )}
            <EntityDataTable
              service={this.service}
              onPagerChange={this.pagerChanged.bind(this)}
              columns={columns}
              listRequest={this.state.listRequest}
              onResults={(response: CashbackSingleCurrencyPayoutListResponse) => this.setState({ warningFlag: response?.warningFlag === true })}
              onAction={() => {
                return new Promise<any>(() => console.log(""));
              }}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}

const withCashbackV2Enabled = withFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED, "cashbackV2Enabled");

export default withRouter(withCashbackV2Enabled(CashbackSingleCurrencyPayoutListView));
